import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { MapPin, Clock, DollarSign, User, Edit, Trash2, CheckCircle, Star, Map, Banknote, CreditCard, LayoutGrid, List, Search, Filter, X, Calendar, Navigation, Globe, Lock, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import RateDriverModal from '../components/RateDriverModal';
import DriverInfoCard from '../components/DriverInfoCard';
import EmbeddedTrackingMap from '../components/EmbeddedTrackingMap';
import RouteVisualizationMap from '../components/RouteVisualizationMap';

const MyRides = () => {
  const { user } = useContext(AuthContext);
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [expandedRide, setExpandedRide] = useState(null);
  const [showMap, setShowMap] = useState({});
  const [showRouteMap, setShowRouteMap] = useState({}); // For route visualization
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  const [ratingModal, setRatingModal] = useState({ open: false, driverId: null, rideId: null, driverName: '' });
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  const navigate = useNavigate();
  const isRider = user?.role === 'rider';
  const isDriver = user?.role === 'driver';

  // Filter rides based on criteria
  const filteredRides = useMemo(() => {
    return rides.filter(ride => {
      // Status filter
      if (statusFilter !== 'all' && ride.status !== statusFilter) {
        return false;
      }
      
      // Search query (client name or phone)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = ride.client_name?.toLowerCase().includes(query);
        const matchesPhone = ride.client_phone?.toLowerCase().includes(query);
        const matchesPickup = ride.pickup_location?.toLowerCase().includes(query);
        const matchesDropoff = ride.dropoff_location?.toLowerCase().includes(query);
        if (!matchesName && !matchesPhone && !matchesPickup && !matchesDropoff) {
          return false;
        }
      }
      
      // Date range filter
      if (dateFrom) {
        const rideDate = new Date(ride.date);
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (rideDate < fromDate) {
          return false;
        }
      }
      
      if (dateTo) {
        const rideDate = new Date(ride.date);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (rideDate > toDate) {
          return false;
        }
      }
      
      return true;
    });
  }, [rides, statusFilter, searchQuery, dateFrom, dateTo]);

  const clearFilters = () => {
    setStatusFilter('all');
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = statusFilter !== 'all' || searchQuery || dateFrom || dateTo;

  useEffect(() => {
    fetchRides();
    console.log('MyRides component loaded - New version with cancel button');
  }, []);

  const fetchRides = async () => {
    try {
      const response = await axios.get(`${API}/rides`);
      setRides(response.data);
    } catch (error) {
      toast.error('Failed to load rides');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/rides/${deleteId}`);
      toast.success('Ride deleted successfully');
      setRides(rides.filter(ride => ride.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      toast.error('Failed to delete ride');
    }
  };

  const handlePickup = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/pickup`);
      toast.success('Rider picked up! Ready for dropoff.');
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as picked up');
    }
  };

  const handleDropoff = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/dropoff`);
      toast.success('Ride completed! Great job!');
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete ride');
    }
  };

  const handleCancelRide = async (rideId) => {
    if (!window.confirm('Are you sure you want to cancel this ride? It will become available for other drivers.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/rides/${rideId}/cancel`);
      toast.success('Ride cancelled. It\'s now available for other drivers.');
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel ride');
    }
  };

  const handleMarkComplete = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/complete`);
      toast.success('Ride marked as completed!');
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark ride as completed');
    }
  };

  const handlePaidCash = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/paid-cash`);
      toast.success('Ride marked as paid in cash');
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment status');
    }
  };

  const handlePaidCard = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/paid-card`);
      toast.success('Ride marked as paid by card');
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment status');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'available': { color: 'bg-blue-100 text-blue-800', label: 'Available' },
      'claimed': { color: 'bg-gold-100 text-gold-800', label: 'Claimed' },
      'assigned': { color: 'bg-purple-100 text-purple-800', label: 'Assigned' },
      'picked_up': { color: 'bg-orange-100 text-orange-800', label: 'In Progress' },
      'completed': { color: 'bg-green-100 text-green-800', label: 'Completed' },
      'requested': { color: 'bg-indigo-100 text-indigo-800', label: 'Requested' },
      'cancelled': { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  const getVisibilityBadge = (ride) => {
    if (!ride.company_id) return null;
    
    if (ride.visibility === 'platform_all') {
      return (
        <Badge variant="outline" className="text-green-600 border-green-200 flex items-center gap-1">
          <Globe className="w-3 h-3" />
          Public
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-gray-500 border-gray-200 flex items-center gap-1">
        <Lock className="w-3 h-3" />
        Company
      </Badge>
    );
  };

  const handleShareToplatform = async (rideId) => {
    if (!user?.company_id || user?.company_role !== 'owner') {
      toast.error('Only company owners can share to platform');
      return;
    }
    try {
      await axios.put(`${API}/companies/${user.company_id}/content/ride/${rideId}/visibility`, {
        visibility: 'platform_all'
      });
      toast.success('Ride shared to platform!');
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share ride');
    }
  };

  const handleMakeCompanyOnly = async (rideId) => {
    if (!user?.company_id || user?.company_role !== 'owner') {
      toast.error('Only company owners can change visibility');
      return;
    }
    try {
      await axios.put(`${API}/companies/${user.company_id}/content/ride/${rideId}/visibility`, {
        visibility: 'company'
      });
      toast.success('Ride is now company-only');
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update visibility');
    }
  };

  const getPaymentBadge = (ride) => {
    if (ride.payment_method === 'cash') {
      return (
        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <Banknote className="w-3 h-3" />
          Cash
        </Badge>
      );
    }
    if (ride.payment_method === 'card' || ride.payment_status === 'paid') {
      return (
        <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
          <CreditCard className="w-3 h-3" />
          Card
        </Badge>
      );
    }
    if (ride.payment_status === 'pending') {
      return (
        <Badge className="bg-gold-100 text-gold-800">
          Payment Pending
        </Badge>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading rides...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">My Rides 🚗</h2>
          <p className="text-gray-600 mt-1">
            {isRider ? 'Your ride requests - Cancel anytime before pickup' : 'Rides you created or are assigned to'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Filter Toggle */}
          <Button
            variant={showFilters ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={hasActiveFilters ? 'ring-2 ring-blue-500' : ''}
          >
            <Filter className="w-4 h-4 mr-1" />
            Filters
            {hasActiveFilters && <span className="ml-1 text-xs bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center">!</span>}
          </Button>
          {/* View Toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'card' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('card')}
              className={`rounded-none ${viewMode === 'card' ? '' : 'bg-white'}`}
              data-testid="card-view-toggle"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={`rounded-none ${viewMode === 'list' ? '' : 'bg-white'}`}
              data-testid="list-view-toggle"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
          {!isRider && (
            <Button onClick={() => navigate('/rides/new')}>
              + New Ride
            </Button>
          )}
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-col lg:flex-row gap-4 items-end">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Client name, phone, or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-white"
                    data-testid="search-input"
                  />
                </div>
              </div>
              
              {/* Status Filter */}
              <div className="w-full lg:w-[180px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-white" data-testid="status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="claimed">Claimed</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="picked_up">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="requested">Requested</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Date From */}
              <div className="w-full lg:w-[160px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="pl-9 bg-white"
                    data-testid="date-from"
                  />
                </div>
              </div>
              
              {/* Date To */}
              <div className="w-full lg:w-[160px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="pl-9 bg-white"
                    data-testid="date-to"
                  />
                </div>
              </div>
              
              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
            
            {/* Results count */}
            <div className="mt-3 text-sm text-gray-600">
              Showing {filteredRides.length} of {rides.length} rides
              {hasActiveFilters && ' (filtered)'}
            </div>
          </CardContent>
        </Card>
      )}

      {filteredRides.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center py-8 text-gray-500">
              {hasActiveFilters 
                ? 'No rides match your filters. Try adjusting your search criteria.' 
                : isRider 
                  ? 'No ride requests yet' 
                  : 'No rides yet. Create your first ride!'}
            </p>
            {hasActiveFilters && (
              <div className="text-center">
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        /* List View */
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Pickup</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Dropoff</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRides.map((ride) => (
                  <tr key={ride.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {format(new Date(ride.date), 'MMM d, yyyy')}
                      <br />
                      <span className="text-gray-500 text-xs">{format(new Date(ride.date), 'h:mm a')}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium">{ride.client_name}</div>
                      {ride.client_phone && <div className="text-xs text-gray-500">{ride.client_phone}</div>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="text-sm text-gray-600 max-w-[200px] truncate" title={ride.pickup_location}>
                        {ride.pickup_location}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="text-sm text-gray-600 max-w-[200px] truncate" title={ride.dropoff_location}>
                        {ride.dropoff_location}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-semibold text-green-600">${ride.earnings?.toFixed(2) || '0.00'}</div>
                      {ride.tips > 0 && <div className="text-xs text-gray-500">+${ride.tips.toFixed(2)} tip</div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {getStatusBadge(ride.status)}
                        {getVisibilityBadge(ride)}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getPaymentBadge(ride) || <span className="text-gray-400 text-xs">-</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Mark Complete button */}
                        {!isRider && ride.status !== 'completed' && ride.status !== 'cancelled' && (ride.assigned_to === user.id || ride.user_id === user.id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkComplete(ride.id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 w-8 p-0"
                            title="Mark Complete"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Cash payment button */}
                        {!isRider && ride.status === 'completed' && !ride.payment_method && (ride.assigned_to === user.id || ride.user_id === user.id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePaidCash(ride.id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 w-8 p-0"
                            title="Paid Cash"
                          >
                            <Banknote className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Card payment button */}
                        {!isRider && ride.status === 'completed' && !ride.payment_method && (ride.assigned_to === user.id || ride.user_id === user.id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePaidCard(ride.id)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                            title="Paid Card"
                          >
                            <CreditCard className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Edit button */}
                        {!isRider && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/rides/edit/${ride.id}`)}
                            className="h-8 w-8 p-0"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Delete button */}
                        {!isRider && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteId(ride.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Summary row */}
          <div className="bg-gray-50 border-t px-4 py-3 flex justify-between items-center">
            <span className="text-sm text-gray-600">
              {filteredRides.length} ride{filteredRides.length !== 1 ? 's' : ''}
              {hasActiveFilters && ` (of ${rides.length} total)`}
            </span>
            <span className="text-sm font-semibold">
              Total: <span className="text-green-600">${filteredRides.reduce((sum, r) => sum + (r.earnings || 0), 0).toFixed(2)}</span>
            </span>
          </div>
        </div>
      ) : (
        /* Card View */
        <div className="grid grid-cols-1 gap-4">
          {filteredRides.map((ride) => (
            <Card key={ride.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-bold flex items-center gap-2">
                        <User className="w-5 h-5 text-blue-600" />
                        {ride.client_name}
                      </h3>
                      {ride.client_phone && (
                        <p className="text-sm text-gray-600">{ride.client_phone}</p>
                      )}
                      {isDriver && ride.assigned_to === user.id && ride.user_id !== user.id && (
                        <p className="text-xs text-blue-600 font-medium mt-1">Assigned to you</p>
                      )}
                      {isDriver && ride.user_id === user.id && ride.assigned_to !== user.id && (
                        <p className="text-xs text-gray-500 mt-1">Created by you</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {getStatusBadge(ride.status)}
                      {getPaymentBadge(ride)}
                      {getVisibilityBadge(ride)}
                    </div>
                  </div>
                  
                  {/* Date & Time */}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{format(new Date(ride.date), 'EEE, MMM dd, yyyy - h:mm a')}</span>
                  </div>
                  
                  {/* Route */}
                  <div className="text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-green-600" />
                      <strong>Pickup:</strong> {ride.pickup_location}
                    </div>
                    {/* Intermediate Stops */}
                    {ride.stops && ride.stops.length > 0 && (
                      <div className="ml-6 my-1 space-y-1">
                        {ride.stops.filter(s => s && s.address).map((stop, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-blue-600">
                            <Badge variant="outline" className="w-5 h-5 flex items-center justify-center text-xs p-0">
                              {idx + 1}
                            </Badge>
                            <span className="text-gray-600">{stop.address}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin className="w-4 h-4 text-red-600" />
                      <strong>Dropoff:</strong> {ride.dropoff_location}
                    </div>
                  </div>

                  {/* Show Route Map Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowRouteMap({ ...showRouteMap, [ride.id]: !showRouteMap[ride.id] })}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 -ml-2"
                  >
                    <Navigation className="w-4 h-4 mr-1" />
                    {showRouteMap[ride.id] ? 'Hide Route' : 'View Route'}
                    {ride.stops && ride.stops.filter(s => s && s.address).length > 0 && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {ride.stops.filter(s => s && s.address).length} stops
                      </Badge>
                    )}
                  </Button>

                  {/* Route Visualization Map */}
                  {showRouteMap[ride.id] && (
                    <div className="mt-2">
                      <RouteVisualizationMap
                        pickup={ride.pickup_location}
                        dropoff={ride.dropoff_location}
                        stops={(ride.stops || []).filter(s => s && s.address)}
                        height="250px"
                        showCard={false}
                      />
                    </div>
                  )}
                  
                  {/* Distance & Time */}
                  <div className="flex gap-4 text-sm text-gray-600">
                    <span>{ride.miles_driven} miles</span>
                    <span>•</span>
                    <span>{(ride.time_hours * 60).toFixed(0)} minutes</span>
                  </div>

                  {/* Driver Info for Riders */}
                  {isRider && ride.assigned_to && expandedRide === ride.id && (
                    <DriverInfoCard driverId={ride.assigned_to} />
                  )}

                  {/* Embedded Tracking Map */}
                  {showMap[ride.id] && (ride.status === 'claimed' || ride.status === 'assigned' || ride.status === 'picked_up') && (
                    <div className="my-4">
                      <EmbeddedTrackingMap ride={ride} />
                    </div>
                  )}

                  {/* Earnings & Actions */}
                  <div className="flex justify-between items-end pt-4 border-t">
                    <div>
                      <div className="text-sm text-gray-600">Total Fare</div>
                      <div className="text-2xl font-bold">${ride.total_amount.toFixed(2)}</div>
                      {!isRider && (
                        <div className="text-sm text-green-600 font-semibold">
                          Your earnings: ${ride.net_earnings.toFixed(2)}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 flex-wrap justify-end">
                      {/* Track on Map button - for active rides */}
                      {(ride.status === 'claimed' || ride.status === 'assigned' || ride.status === 'picked_up') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowMap({ ...showMap, [ride.id]: !showMap[ride.id] })}
                          className="bg-blue-50 hover:bg-blue-100 text-blue-700"
                        >
                          <Map className="w-4 h-4 mr-1" />
                          {showMap[ride.id] ? 'Hide Map' : 'Track on Map'}
                        </Button>
                      )}

                      {/* Rider Actions */}
                      {isRider && (
                        <>
                          {ride.assigned_to && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setExpandedRide(expandedRide === ride.id ? null : ride.id)}
                            >
                              {expandedRide === ride.id ? 'Hide' : 'View'} Driver
                            </Button>
                          )}
                          
                          {ride.status === 'completed' && ride.assigned_to && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                const driverResponse = await axios.get(`${API}/profile/driver/${ride.assigned_to}`);
                                setRatingModal({
                                  open: true,
                                  driverId: ride.assigned_to,
                                  rideId: ride.id,
                                  driverName: driverResponse.data.name
                                });
                              }}
                              className="bg-gold-50 hover:bg-gold-100 text-gold-700"
                            >
                              <Star className="w-4 h-4 mr-1" />
                              Rate Driver
                            </Button>
                          )}
                          
                          {/* Cancel button for non-completed rides */}
                          {(ride.status !== 'completed' && ride.status !== 'picked_up') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancelRide(ride.id)}
                              className="bg-red-50 text-red-600 hover:text-red-700 hover:bg-red-100 border-red-200"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Cancel Ride
                            </Button>
                          )}
                        </>
                      )}

                      {/* Driver Actions */}
                      {!isRider && (
                        <>
                          {/* Mark as Completed button - for assigned rides that aren't completed */}
                          {ride.status !== 'completed' && ride.status !== 'cancelled' && (ride.assigned_to === user.id || ride.user_id === user.id) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleMarkComplete(ride.id)}
                              className="bg-green-50 hover:bg-green-100 text-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Mark Complete
                            </Button>
                          )}

                          {/* Payment buttons - show for completed rides without payment method set */}
                          {ride.status === 'completed' && !ride.payment_method && (ride.assigned_to === user.id || ride.user_id === user.id) && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePaidCash(ride.id)}
                                className="bg-green-50 hover:bg-green-100 text-green-700"
                              >
                                <Banknote className="w-4 h-4 mr-1" />
                                Paid Cash
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePaidCard(ride.id)}
                                className="bg-blue-50 hover:bg-blue-100 text-blue-700"
                              >
                                <CreditCard className="w-4 h-4 mr-1" />
                                Paid Card
                              </Button>
                            </>
                          )}

                          {/* Pickup button */}
                          {(ride.status === 'claimed' || ride.status === 'assigned' || ride.status === 'requested') && ride.assigned_to === user.id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePickup(ride.id)}
                              className="bg-orange-50 hover:bg-orange-100 text-orange-700"
                            >
                              <MapPin className="w-4 h-4 mr-1" />
                              Pick Up
                            </Button>
                          )}
                          
                          {/* Dropoff button */}
                          {ride.status === 'picked_up' && ride.assigned_to === user.id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDropoff(ride.id)}
                              className="bg-green-50 hover:bg-green-100 text-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Drop Off
                            </Button>
                          )}
                          
                          {/* Cancel button for assigned drivers (not completed/picked up) */}
                          {ride.status !== 'completed' && ride.status !== 'picked_up' && ride.assigned_to === user.id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancelRide(ride.id)}
                              className="bg-red-50 text-red-600 hover:text-red-700 hover:bg-red-100 border-red-200"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          )}
                          
                          {/* Edit/Delete for completed or own rides */}
                          {(ride.status === 'completed' || ride.status === 'cancelled' || ride.user_id === user.id) && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/rides/edit/${ride.id}`)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDeleteId(ride.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          
                          {/* Share to Platform button for company owners */}
                          {ride.company_id && user?.company_role === 'owner' && ride.visibility === 'company' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleShareToplatform(ride.id)}
                              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                              title="Share ride to entire platform"
                            >
                              <Globe className="w-4 h-4 mr-1" />
                              Share
                            </Button>
                          )}
                          
                          {/* Make Company Only button */}
                          {ride.company_id && user?.company_role === 'owner' && ride.visibility === 'platform_all' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleMakeCompanyOnly(ride.id)}
                              className="bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200"
                              title="Make ride company-only"
                            >
                              <Lock className="w-4 h-4 mr-1" />
                              Private
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Rating Modal */}
      <RateDriverModal
        open={ratingModal.open}
        onClose={() => setRatingModal({ open: false, driverId: null, rideId: null, driverName: '' })}
        driverId={ratingModal.driverId}
        rideId={ratingModal.rideId}
        driverName={ratingModal.driverName}
        onRatingSubmitted={() => fetchRides()}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ride</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this ride? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyRides;
